// Ends a bare URL autolink before full-width (CJK) characters.
//
// marked's GFM autolink (the inline `url` tokenizer) takes everything up to a
// space or `<`. English puts a space after a URL; Japanese does not, so the rest
// of the sentence became part of the link (with K for Japanese text and a
// full-width parenthesis around the URL):
//
//   K(https://example.com)KKK  -> href `https://example.com)KKK`
//   K https://example.com/a?b=1KK  -> href `https://example.com/a?b=1KK`
//
// and serializing wrote it back as a wrong explicit link.
//
// The built-in result is used, and its raw text is cut before the first
// non-ASCII character (code point > 0x7E: CJK and full-width punctuation). The
// GFM trailing rules (drop `?!.,:*_~`, drop `)` while there are more `)` than
// `(`) are applied again. The `http://` prefix of `www.` links and the
// `mailto:` prefix of mail links are kept from the built-in result. If nothing
// is left, return false so marked reads it as text. Only bare URLs: `[x](url)`
// and `<url>` are untouched (a URL with Japanese in it can be written that way).
import { type MarkedExtension, Tokenizer } from "marked";

const builtinUrl = Tokenizer.prototype.url;

/** Index of the first non-ASCII character, or -1. */
function firstNonAscii(s: string): number {
  for (let i = 0; i < s.length; i++) {
    if (s.charCodeAt(i) > 0x7e) return i;
  }
  return -1;
}

function count(s: string, ch: string): number {
  return s.split(ch).length - 1;
}

/** GFM's handling of the end of a URL. */
function trimTrailing(url: string): string {
  let s = url;
  for (;;) {
    const last = s.slice(-1);
    if ("?!.,:*_~".includes(last) && last !== "") s = s.slice(0, -1);
    else if (last === ")" && count(s, ")") > count(s, "(")) s = s.slice(0, -1);
    else return s;
  }
}

export const cjkAutolink: MarkedExtension = {
  tokenizer: {
    url(src) {
      const tok = builtinUrl.call(this, src);
      if (tok?.type !== "link") return tok ?? false;
      const cut = firstNonAscii(tok.raw);
      if (cut < 0) return tok;
      const raw = trimTrailing(tok.raw.slice(0, cut));
      if (raw === "") return false;
      // The built-in href is prefix (`mailto:` / `http://` / none) + raw.
      const prefix = tok.href.slice(0, tok.href.length - tok.raw.length);
      return {
        type: "link",
        raw,
        text: raw,
        href: prefix + raw,
        tokens: [{ type: "text", raw, text: raw }],
      };
    },
  },
};
